import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Novedades.
 *
 * Los tipos están escritos a mano y eso es **deuda declarada, no una decisión**:
 * el backend declara un solo `response_model` en 27 routers, así que
 * `/openapi.json` no describe ninguna respuesta y no hay nada que generar. Están
 * copiados leyendo `content.py` campo por campo — `_post_dict` (línea 1620) es
 * la función que arma cada publicación.
 *
 * ## Los endpoints NO cambian
 *
 * Son las mismas catorce rutas que ya pedía `page-feed.js`, con los mismos
 * parámetros. Dos peticiones **dejan de hacerse**, y ninguna es un cambio de
 * contrato:
 *
 *   · `/users/me/profile` — su único uso era la foto del avatar del compositor,
 *     y el shell ya la resolvió en `startApp()`. Es la misma que Inicio dejó de
 *     pedir el 2026-09-07, por lo mismo.
 *
 *   · `/kudos/leaderboard` cuando el rol no tiene el módulo. Ver
 *     `components/PanelesDeEquipo.tsx`.
 *
 * Y una cambia de momento, no de forma: `/admin/moderation/words/effective` se
 * pedía al montar la pantalla; ahora se pide al **abrir el compositor**, que es
 * cuando la lista hace falta. Quien solo entra a leer el muro no la pide.
 *
 * ## Lo que esta capa hace y la vanilla no: escapar
 *
 * Nada de aquí sale como marcado. El único HTML que se inserta es el contenido
 * enriquecido de la publicación, y pasa por `Utils.renderPostContent` —el mismo
 * saneador de siempre— en `contenido.tsx`. Los diez sitios donde
 * `page-feed.js` interpolaba texto de una persona en una plantilla (el nombre
 * del autor, el cargo, el área, la audiencia, los nombres de quien no ha leído)
 * son ahora nodos de texto de React, que escapa por construcción.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

export interface Adjunto {
  id: number;
  filename: string;
  url: string;
  content_type?: string | null;
  size_bytes?: number | null;
  is_image?: boolean;
  is_pdf?: boolean;
}

export interface FotoDeGaleria {
  url: string;
  thumb_url?: string | null;
}

export interface Publicacion {
  id: number;
  post_type: string;
  /** HTML enriquecido del compositor. Se pinta con `Utils.renderPostContent`. */
  content: string;
  created_at: string;

  author_id?: number | null;
  /** Cuidado: NO siempre es el nombre del directorio. Al firmar a nombre del
   *  área, `content.py:1634` devuelve `nombre_publico` — 120 caracteres de
   *  texto libre que escribe quien publica. Es el campo de mayor exposición de
   *  esta pantalla. */
  author_name?: string | null;
  author_role?: string | null;
  author_position?: string | null;
  author_area?: string | null;
  author_avatar?: string | null;
  /** Quién publicó de verdad tras una firma institucional. Solo viaja para
   *  staff (`content.py:1683`). */
  author_real?: string | null;
  author_is_manager?: boolean;

  image_url?: string | null;
  gallery?: FotoDeGaleria[];
  attachments?: Adjunto[];

  reaction_count?: number;
  comment_count?: number;
  user_reaction?: string | null;

  requires_ack?: boolean;
  has_read?: boolean;
  /** Texto que resume la audiencia de un comunicado. Lo arma el servidor con
   *  nombres de área, cargo o etiqueta — o sea, texto que escribió alguien. */
  audience_label?: string | null;

  /** Retirada. La tarjeta se conserva con sus comentarios (decisión del
   *  2026-08-04): quien comentó no ve desaparecer su comentario. */
  eliminada?: boolean;
  eliminada_por_su_autor?: boolean;

  survey_id?: number | null;
}

export interface Comentario {
  id: number;
  content: string;
  created_at: string;
  author_id?: number | null;
  author_name?: string | null;
}

export interface Reaccion {
  full_name: string;
  reaction_type: string;
}

export interface Metricas {
  audience_label?: string | null;
  requires_ack?: boolean;
  reach: number;
  reactions: number;
  comments: number;
  reads: number;
  read_rate: number;
  pending_count?: number;
  /** El servidor recorta la muestra a 50 nombres. */
  pending_sample?: string[];
  /** Audiencia configurada que no alcanzó a nadie. Sin esto, un comunicado con
   *  alcance 0 anunciaba «Todos confirmaron lectura». */
  sin_destinatarios?: boolean;
}

export interface PersonaDelDirectorio {
  id: number;
  full_name: string;
  position?: string | null;
}

/* ── Filtros ───────────────────────────────────────────────────────────── */

export interface Filtros {
  /** `post_type`. Cadena vacía = todos. */
  tipo: string;
  autor: { id: number; nombre: string } | null;
  busqueda: string;
  /** Días hacia atrás. `null` = cualquier fecha. */
  dias: number | null;
}

export const FILTROS_VACIOS: Filtros = { tipo: '', autor: null, busqueda: '', dias: null };

export function hayFiltro(f: Filtros) {
  return Boolean(f.tipo || f.autor || f.busqueda || f.dias);
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  muro: (f: Filtros) =>
    ['novedades', 'muro', f.tipo, f.autor?.id ?? null, f.busqueda, f.dias] as const,
  comentarios: (id: number) => ['novedades', 'comentarios', id] as const,
  reacciones: (id: number) => ['novedades', 'reacciones', id] as const,
  metricas: (id: number) => ['novedades', 'metricas', id] as const,
  autores: (q: string) => ['novedades', 'autores', q] as const,
};

/** Cuántas publicaciones por página. Es el `limit` que ya usaba la vanilla. */
export const POR_PAGINA = 20;

/* ── El muro ───────────────────────────────────────────────────────────── */

/**
 * Las publicaciones, paginadas.
 *
 * `useInfiniteQuery` sobre el mismo `skip`/`limit` de siempre. Lo que cambia es
 * que la vanilla llevaba la página en una variable de módulo (`_page`) y el
 * observador la incrementaba; si dos cargas se solapaban —un filtro pulsado
 * mientras bajaba una página— se perdía el sitio. Aquí la página es del
 * llamador y el filtro es parte de la clave, así que cambiar de filtro empieza
 * una consulta distinta en vez de mezclarse con la anterior.
 *
 * `require_module("feed")` en el servidor (`content.py:246`).
 */
export function useMuro(filtros: Filtros) {
  return useInfiniteQuery({
    queryKey: claves.muro(filtros),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params: Record<string, string | number> = {
        skip: pageParam * POR_PAGINA,
        limit: POR_PAGINA,
      };
      if (filtros.tipo) params.post_type = filtros.tipo;
      if (filtros.autor) params.author_id = filtros.autor.id;
      if (filtros.busqueda) params.search = filtros.busqueda;
      if (filtros.dias) params.days = filtros.dias;
      return api.get<Publicacion[]>('/posts', params);
    },
    // Una página incompleta es la última. El endpoint no devuelve el total.
    getNextPageParam: (ultima, todas) =>
      ultima.length < POR_PAGINA ? undefined : todas.length,
  });
}

/* ── Comentarios ───────────────────────────────────────────────────────── */

export function useComentarios(postId: number, abierto: boolean) {
  return useQuery({
    queryKey: claves.comentarios(postId),
    queryFn: () => api.get<Comentario[]>(`/posts/${postId}/comments`),
    enabled: abierto,
  });
}

/* ── Reacciones ────────────────────────────────────────────────────────── */

export function useReacciones(postId: number, abierto: boolean) {
  return useQuery({
    queryKey: claves.reacciones(postId),
    queryFn: () => api.get<Reaccion[]>(`/posts/${postId}/reactions`),
    enabled: abierto,
  });
}

/* ── Métricas ──────────────────────────────────────────────────────────── */

export function useMetricas(postId: number, abierto: boolean) {
  return useQuery({
    queryKey: claves.metricas(postId),
    queryFn: () => api.get<Metricas>(`/posts/${postId}/metrics`),
    enabled: abierto,
  });
}

/* ── Buscador de autores ───────────────────────────────────────────────── */

/** El desplegable del filtro por autor. Por debajo de dos letras no pregunta:
 *  con una sola, `/users/directory` devuelve media organización. */
export function useAutores(busqueda: string) {
  const q = busqueda.trim();
  return useQuery({
    queryKey: claves.autores(q),
    queryFn: () =>
      api
        .get<{ items?: PersonaDelDirectorio[] }>('/users/directory', { search: q, limit: 8 })
        .then((r) => r.items ?? []),
    enabled: q.length >= 2,
  });
}

/* ── Mutaciones ────────────────────────────────────────────────────────── */

/** Invalida el muro entero, sea cual sea el filtro activo. */
function useRefrescarMuro() {
  const cliente = useQueryClient();
  return () => void cliente.invalidateQueries({ queryKey: ['novedades', 'muro'] });
}

export interface Borrador {
  /** HTML ya saneado por `Utils.sanitizeRich`. */
  contenido: string;
  tipo: string;
  imagen: File | null;
  adjuntos: File[];
  /** Firma a nombre del área. */
  comoArea: boolean;
  nombrePublico: string;
}

export interface ResultadoDePublicar {
  id: number;
  /** Los adjuntos que no subieron. `POST /posts/{id}/attachments` es
   *  reintentable, así que se ofrece reintentar en vez de dejar a la persona
   *  con la publicación arriba y sin su archivo. */
  fallidos: File[];
  imagenFallo: boolean;
}

export function usePublicar() {
  const refrescar = useRefrescarMuro();
  return useMutation({
    mutationFn: async (b: Borrador): Promise<ResultadoDePublicar> => {
      const cuerpo: Record<string, unknown> = { content: b.contenido, post_type: b.tipo };
      if (b.comoArea) {
        cuerpo.publicar_como_area = true;
        const nombre = b.nombrePublico.trim();
        if (nombre) cuerpo.nombre_publico = nombre;
      }
      const creada = await api.post<{ id: number }>('/posts', cuerpo);

      let imagenFallo = false;
      if (b.imagen) {
        const fd = new FormData();
        fd.append('file', b.imagen);
        try {
          await api.upload(`/posts/${creada.id}/image`, fd);
        } catch {
          imagenFallo = true;
        }
      }

      const fallidos = await subirAdjuntos(creada.id, b.adjuntos);
      return { id: creada.id, fallidos, imagenFallo };
    },
    onSuccess: refrescar,
  });
}

/** Sube uno a uno y devuelve los que no pasaron. Uno a uno y no en lote porque
 *  el endpoint acepta un `file` por llamada, y así un archivo grande que falla
 *  no arrastra a los que ya subieron. */
export async function subirAdjuntos(postId: number, archivos: File[]): Promise<File[]> {
  const fallidos: File[] = [];
  for (const f of archivos) {
    const fd = new FormData();
    fd.append('file', f);
    try {
      await api.upload(`/posts/${postId}/attachments`, fd);
    } catch {
      fallidos.push(f);
    }
  }
  return fallidos;
}

export function useReaccionar() {
  return useMutation({
    mutationFn: ({ postId, reaccion }: { postId: number; reaccion: string }) =>
      api.post(`/posts/${postId}/react`, { reaction_type: reaccion }),
  });
}

export function useComentar(postId: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (contenido: string) =>
      api.post(`/posts/${postId}/comments`, { content: contenido }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.comentarios(postId) }),
  });
}

export function useBorrarComentario(postId: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (comentarioId: number) => api.del(`/comments/${comentarioId}`),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.comentarios(postId) }),
  });
}

export function useRetirarPublicacion() {
  const refrescar = useRefrescarMuro();
  return useMutation({
    // El motivo viaja en la URL porque así lo declara el endpoint hoy
    // (`content.py`, `motivo` como query param). Cambiar esa firma no es un
    // rediseño; queda anotado en la ficha del módulo.
    mutationFn: ({ postId, motivo }: { postId: number; motivo: string }) => {
      const qs = motivo.trim() ? `?motivo=${encodeURIComponent(motivo.trim())}` : '';
      return api.del<{ message?: string }>(`/posts/${postId}${qs}`);
    },
    onSuccess: refrescar,
  });
}

export function useDenunciarPublicacion() {
  return useMutation({
    mutationFn: ({ postId, motivo }: { postId: number; motivo: string }) =>
      api.post<{ message?: string }>(`/posts/${postId}/report`, { reason: motivo }),
  });
}

export function useDenunciarComentario() {
  return useMutation({
    mutationFn: ({ comentarioId, motivo }: { comentarioId: number; motivo: string }) =>
      api.post<{ message?: string }>(`/comments/${comentarioId}/report`, { reason: motivo }),
  });
}

export function useConfirmarLectura() {
  const refrescar = useRefrescarMuro();
  return useMutation({
    mutationFn: (postId: number) => api.post(`/posts/${postId}/read`, null),
    onSuccess: refrescar,
  });
}

export function useBorrarAdjunto() {
  const refrescar = useRefrescarMuro();
  return useMutation({
    mutationFn: ({ postId, adjuntoId }: { postId: number; adjuntoId: number }) =>
      api.del(`/posts/${postId}/attachments/${adjuntoId}`),
    onSuccess: refrescar,
  });
}
